/** 二维码渲染：优先 node-qrcode（更清晰边距），模板回退 qrcodejs */

let qrcodeModulePromise = null

const QR_LIB_ID = 'qrcodejs-lib'
let qrLoadPromise = null

const QRCODE_ESM_URL = 'https://cdn.jsdelivr.net/npm/qrcode@1.5.4/+esm'

function localQrLibUrl () {
  const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
  return `${base}js/qrcode.min.js`
}

const CDN_QR_LIB_URL = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'

function loadScript (src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = QR_LIB_ID
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      script.remove()
      reject(new Error('load failed: ' + src))
    }
    document.head.appendChild(script)
  })
}

function ensureQrcodeModule () {
  if (!qrcodeModulePromise) {
    // 运行时从 CDN 加载 ESM；webpackIgnore 避免「dependency is an expression」告警
    qrcodeModulePromise = import(/* webpackIgnore: true */ QRCODE_ESM_URL)
      .then((mod) => mod.default || mod)
      .catch(() => null)
  }
  return qrcodeModulePromise
}

export function ensureQrLibLoaded () {
  return ensureQrcodeModule().then((mod) => {
    if (mod && mod.toDataURL) {
      return mod
    }
    if (typeof window !== 'undefined' && window.QRCode) {
      return window.QRCode
    }
    if (qrLoadPromise) {
      return qrLoadPromise
    }
    qrLoadPromise = loadScript(localQrLibUrl())
      .catch(() => loadScript(CDN_QR_LIB_URL))
      .then(() => {
        if (!window.QRCode) {
          throw new Error('QRCode not available')
        }
        return window.QRCode
      })
      .catch((err) => {
        qrLoadPromise = null
        throw err
      })
    return qrLoadPromise
  })
}

export function isDenseQrText (text) {
  const raw = String(text || '').trim()
  if (!raw) {
    return false
  }
  if (raw.startsWith('mbspay://')) {
    return true
  }
  return raw.length > 400
}

export function computeQrPixelSize (text, options = {}) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 390
  const raw = String(text || '').trim()
  if (options.pixelSize) {
    return options.pixelSize
  }
  if (!isDenseQrText(raw)) {
    return Math.max(220, Math.min(300, Math.floor(vw * 0.72)))
  }
  return Math.max(260, Math.min(320, Math.floor(vw * 0.78)))
}

function resolveErrorCorrectionLevel (text, options = {}) {
  if (options.errorCorrectionLevel) {
    return options.errorCorrectionLevel
  }
  return isDenseQrText(text) ? 'M' : 'L'
}

function renderWithNodeQrcode (QRCode, text, displayEdge, options = {}) {
  return QRCode.toDataURL(text, {
    width: displayEdge,
    margin: options.margin != null ? options.margin : 2,
    errorCorrectionLevel: resolveErrorCorrectionLevel(text, options),
    color: {
      dark: '#000000ff',
      light: '#ffffffff'
    }
  })
}

function mapQrcodejsLevel (level) {
  const lv = String(level || 'L').toUpperCase()
  const CorrectLevel = window.QRCode && window.QRCode.CorrectLevel
  if (!CorrectLevel) {
    return undefined
  }
  if (lv === 'M') return CorrectLevel.M
  if (lv === 'Q') return CorrectLevel.Q
  if (lv === 'H') return CorrectLevel.H
  return CorrectLevel.L
}

function renderWithQrcodejs (text, displayEdge, options = {}) {
  return new Promise((resolve) => {
    const host = document.createElement('div')
    host.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none;'
    document.body.appendChild(host)
    try {
      const correctLevel = mapQrcodejsLevel(resolveErrorCorrectionLevel(text, options))
      // eslint-disable-next-line no-new
      new window.QRCode(host, {
        text: text,
        width: displayEdge,
        height: displayEdge,
        correctLevel: correctLevel || window.QRCode.CorrectLevel.L
      })
      requestAnimationFrame(() => {
        const img = host.querySelector('img')
        const canvas = host.querySelector('canvas')
        let src = ''
        if (img && img.src) {
          src = img.src
        } else if (canvas && canvas.toDataURL) {
          src = canvas.toDataURL('image/png')
        }
        if (host.parentNode) {
          host.parentNode.removeChild(host)
        }
        resolve(src || null)
      })
    } catch (e) {
      if (host.parentNode) {
        host.parentNode.removeChild(host)
      }
      resolve(null)
    }
  })
}

/** 离屏生成二维码 data URL；options 可传 pixelSize / errorCorrectionLevel / margin */
export function buildQrImageSrc (text, options = {}) {
  const raw = String(text || '').trim()
  if (!raw) {
    return Promise.resolve(null)
  }
  const displayEdge = computeQrPixelSize(raw, options)
  return ensureQrLibLoaded()
    .then((lib) => {
      if (lib && lib.toDataURL) {
        return renderWithNodeQrcode(lib, raw, displayEdge, options)
      }
      if (lib && lib.CorrectLevel) {
        return renderWithQrcodejs(raw, displayEdge, options)
      }
      return null
    })
    .catch(() => null)
}

export function paintQrNode (containerEl, edge) {
  if (!containerEl) {
    return
  }
  const img = containerEl.querySelector('img')
  if (img) {
    img.style.display = 'block'
    img.style.margin = '0 auto'
    img.style.width = `${edge}px`
    img.style.maxWidth = '100%'
    img.style.height = 'auto'
    img.classList.add('qr-rendered')
  }
}

export function renderQrInto (containerEl, text, options = {}) {
  if (!containerEl || !text) {
    return Promise.resolve(false)
  }
  return buildQrImageSrc(text, options).then((src) => {
    if (!src) {
      return false
    }
    const edge = computeQrPixelSize(text, options)
    containerEl.innerHTML = ''
    const img = document.createElement('img')
    img.src = src
    img.alt = '支付码'
    img.className = 'qr-rendered'
    img.style.width = `${edge}px`
    img.style.maxWidth = '100%'
    img.style.height = 'auto'
    containerEl.appendChild(img)
    return true
  })
}

/** 从已渲染的二维码容器取出 PNG data URL（img 或 canvas），供长按保存弹层使用。 */
export function captureQrDataUrl (containerEl) {
  if (!containerEl) {
    return ''
  }
  const img = containerEl.querySelector('img')
  if (img && img.src) {
    return img.src
  }
  const canvas = containerEl.querySelector('canvas')
  if (canvas && typeof canvas.toDataURL === 'function') {
    try {
      return canvas.toDataURL('image/png')
    } catch (e) {
      return ''
    }
  }
  return ''
}
